import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useBrand } from '@/config/brands';

export type EmailTopic = 'compliance' | 'pricing' | 'legislation' | 'general';

interface EmailSubscriptionState {
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  subscribe: (email: string, stateCode?: string, topics?: EmailTopic[]) => Promise<void>;
  unsubscribe: (email: string) => Promise<void>;
  updatePreferences: (email: string, stateCode?: string, topics?: EmailTopic[]) => Promise<void>;
}

export function useEmailSubscription(): EmailSubscriptionState {
  const brand = useBrand();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subscribe = useCallback(
    async (
      email: string,
      stateCode?: string,
      topics: EmailTopic[] = ['compliance', 'pricing', 'legislation', 'general']
    ) => {
      if (!email || !email.includes('@')) {
        setError('Please enter a valid email address');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // SECURITY DEFINER RPC (2026-08-02). Was a direct .upsert(), which failed
        // for every visitor with 42501: anon holds INSERT/UPDATE on
        // email_subscriptions but not SELECT, and ON CONFLICT DO UPDATE reads the
        // conflicting row. Granting anon SELECT would expose the entire mailing
        // list — every subscriber's address — to anyone with the public anon key.
        // The RPC writes one row and returns nothing. It also lower/trims and
        // validates the address server-side, so the client check is no longer the
        // only gate.
        const { error: dbError } = await supabase.rpc(
          'register_email_subscription' as any,
          {
            p_email: email,
            p_state_code: stateCode || null,
            p_topics: topics,
            p_brand: brand.key,
          } as any
        );

        if (dbError) throw dbError;
        setIsSubscribed(true);
      } catch (err: any) {
        setError(err.message || 'Failed to subscribe');
        console.error('Email subscription error:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [brand.key]
  );

  const unsubscribe = useCallback(
    async (email: string) => {
      setIsLoading(true);
      setError(null);
      try {
        // Via RPC — same reason: UPDATE ... WHERE email = $1 needs SELECT on the
        // email column, which anon does not have and must not be given.
        const { error: dbError } = await supabase.rpc(
          'update_email_subscription' as any,
          { p_email: email, p_brand: brand.key, p_is_active: false } as any
        );

        if (dbError) throw dbError;
        setIsSubscribed(false);
      } catch (err: any) {
        setError(err.message || 'Failed to unsubscribe');
      } finally {
        setIsLoading(false);
      }
    },
    [brand.key]
  );

  const updatePreferences = useCallback(
    async (email: string, stateCode?: string, topics?: EmailTopic[]) => {
      setIsLoading(true);
      setError(null);
      try {
        // Via RPC for the same permission reason. Nulls are COALESCEd server-side,
        // so changing only topics leaves state_code untouched.
        const { error: dbError } = await supabase.rpc(
          'update_email_subscription' as any,
          {
            p_email: email,
            p_brand: brand.key,
            p_state_code: stateCode !== undefined ? (stateCode || null) : null,
            p_topics: topics ?? null,
          } as any
        );

        if (dbError) throw dbError;
      } catch (err: any) {
        setError(err.message || 'Failed to update preferences');
      } finally {
        setIsLoading(false);
      }
    },
    [brand.key]
  );

  return {
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    updatePreferences,
  };
}
